import { inject, Injectable, computed } from '@angular/core';
import { Dtos } from '#models';
import { Server } from '#enums';
import { AuthService } from './auth/auth.service';
import { StoreService } from './store';

/**
 * Service to centralize participant permission logic for both Projects and PSSRs.
 * Provides methods to check if a user can edit specific participants.
 */
@Injectable({
  providedIn: 'root',
})
export class ParticipantValidator {
  private _authService = inject(AuthService);
  private _storeService = inject(StoreService);

  /**
   * Checks if the current user can edit a specific participant in a Project.
   *
   * User can edit if:
   * - User is the participant themselves, OR
   * - User is Admin (SysAdmin/BuAdmin/OrgAdmin), OR
   * - User is Project Lead
   *
   * And project is not deleted.
   *
   * @param participant The participant to check edit permissions for
   * @param project The project context
   * @param projectUsers All users in the project
   * @returns true if user can edit this participant, false otherwise
   */
  canEditProjectParticipant(participant: Dtos.PSSRUser, project: Dtos.Project, projectUsers: Dtos.PSSRUser[]): boolean {
    const userDetails = this._authService.getUserDetails();
    if (!userDetails || !project) {
      return false;
    }

    // Check if project is deleted
    if (project.IsDeleted) {
      return false;
    }

    // Check if user is the participant themselves
    const isOwnParticipant = userDetails.email?.toLowerCase() === participant.UserEmail?.toLowerCase();

    // Check if user is admin (SysAdmin, BuAdmin, or OrgAdmin)
    const hasAdminPermission =
      userDetails.isSysAdmin || project.CurrentUser?.IsBusinessUnitAdmin || project.CurrentUser?.IsOrgAdmin;

    // Check if user is Project Lead (Role 1)
    const isProjectLead = projectUsers.some(
      (u: Dtos.PSSRUser) => u.UserEmail?.toLowerCase() === userDetails.email?.toLowerCase() && u.Role === 1
    );

    // Allow edit if any of the conditions are met
    return isOwnParticipant || hasAdminPermission || isProjectLead;
  }

  /**
   * Checks if the current user can edit a specific participant in a PSSR.
   *
   * User can edit if:
   * - User is the participant themselves, OR
   * - User is Lead/Approver and linked to participant (shared users), OR
   * - User is BU Admin, OR
   * - User has CanEdit permission
   *
   * And global conditions are met:
   * - Status allows managing participants
   * - PSSR is not deleted
   *
   * @param participant The participant to check edit permissions for
   * @param pssr The PSSR context
   * @param businessUnit The business unit associated with the PSSR
   * @returns true if user can edit this participant, false otherwise
   */
  canEditPssrParticipant(participant: Dtos.PSSRUser, pssr: Dtos.PSSR, businessUnit: Dtos.BusinessUnit): boolean {
    const currentUser = pssr.CurrentUser;
    const userDetails = this._authService.getUserDetails();

    if (!currentUser || !userDetails || !pssr || !businessUnit) {
      return false;
    }

    // Check if status allows managing participants
    if (!businessUnit.PssrStatusesCanManageParticipants.includes(pssr.Status)) {
      return false;
    }

    // Check if PSSR is deleted
    if (pssr.IsDeleted) {
      return false;
    }

    // Granular validation per participant:
    const isOwnParticipant = currentUser.UserID === participant.UserID;

    const isLeadOrApprover = currentUser.Role === Server.UserRole.Lead || currentUser.Role === Server.UserRole.Approver;

    const isLinked = this.isUserLinkedToParticipant(participant, currentUser, userDetails);

    const isBUAdmin = currentUser.IsBusinessUnitAdmin || false;

    const canEdit = currentUser.CanEdit || false;

    // Allow edit if any of the conditions are met
    return isOwnParticipant || (isLeadOrApprover && isLinked) || isBUAdmin || canEdit;
  }

  /**
   * Checks if the current user is linked to a participant through parent/child user relationships.
   *
   * @param participant The participant to check linkage with
   * @param currentUser The current PSSR user
   * @param userDetails The authenticated user details
   * @returns true if users are linked, false otherwise
   */
  private isUserLinkedToParticipant(
    participant: Dtos.PSSRUser,
    currentUser: Dtos.PSSRUser,
    userDetails: { email?: string }
  ): boolean {
    const currentUserIsChildOfParticipant = participant.ChildUsers?.includes(userDetails.email ?? '') ?? false;

    const participantIsChildOfCurrentUser = currentUser.ParentUsers?.includes(participant.UserEmail) ?? false;

    return currentUserIsChildOfParticipant || participantIsChildOfCurrentUser;
  }

  /**
   * Determines if participants can be managed in the current PSSR
   * Rules:
   * - PSSR must not be deleted
   * - PSSR status must allow managing participants (per business unit configuration)
   * - If PSSR status >= Approved: user must be Business Unit Admin
   * - If PSSR status < Approved: user must be BU Admin, Org Admin, or have CanEdit permission
   */
  canManageParticipants = computed(() => {
    const pssr = this._storeService.currentPssr();
    const bu = this._storeService.currentBu();

    if (!pssr || !bu || pssr.IsDeleted) {
      return false;
    }

    if (!bu.PssrStatusesCanManageParticipants.includes(pssr.Status)) {
      return false;
    }

    const currentUser = pssr.CurrentUser;

    // For Approved or higher statuses: only BU Admin can manage
    if (pssr.Status >= Server.PSSRStatus.Approved) {
      return currentUser?.IsBusinessUnitAdmin || false;
    }

    // For lower statuses: BU Admin, Org Admin, or CanEdit permission
    return currentUser?.IsBusinessUnitAdmin || currentUser?.IsOrgAdmin || currentUser?.CanEdit || false;
  });

  /**
   * Determines if the current user can edit participants (alias for canManageParticipants)
   * Returns the same permission check as canManageParticipants
   */
  canEditParticipants = computed(() => this.canManageParticipants());

  /**
   * Determines if the current user can edit a specific participant in the current PSSR
   * Wrapper around canEditPssrParticipant that gets PSSR and business unit from store
   *
   * @param participant The participant to check edit permissions for
   * @returns true if user can edit this participant, false otherwise
   */
  canEditSpecificParticipant(participant: Dtos.PSSRUser): boolean {
    const pssr = this._storeService.currentPssr();
    const bu = this._storeService.currentBu();

    if (!pssr || !bu) {
      return false;
    }

    return this.canEditPssrParticipant(participant, pssr, bu);
  }
}
